import React from 'react';
import {
  Dialog as HeadlessDialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Description,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cx } from './cx';
import { IconButton } from './IconButton';

const SIZES = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl' };

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  size?: keyof typeof SIZES;
  /** Hides the ✕ in the header, for a dialog that must be answered with its own buttons. */
  hideClose?: boolean;
  /** DialogBody and DialogFooter, either directly or inside a <form>. */
  children: React.ReactNode;
}

/**
 * A modal with a header, a scrolling body and a footer bar. Put the content in DialogBody and the
 * buttons in DialogFooter; for a form, wrap both in the <form> so Enter submits it.
 */
export function Dialog({ open, onClose, title, description, size = 'md', hideClose, children }: DialogProps) {
  return (
    <HeadlessDialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-[2px] transition-opacity duration-200 ease-out data-[closed]:opacity-0 motion-reduce:transition-none"
      />
      <div className="fixed inset-0 flex items-end justify-center p-4 sm:items-center">
        <DialogPanel
          transition
          className={cx(
            'flex max-h-[90vh] w-full flex-col rounded-2xl bg-white shadow-2xl dark:bg-gray-900 dark:shadow-none dark:ring-1 dark:ring-gray-800',
            'transition duration-200 ease-out data-[closed]:translate-y-2 data-[closed]:scale-[0.98] data-[closed]:opacity-0 motion-reduce:transition-none',
            SIZES[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-1 pt-5">
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold text-gray-900 text-balance dark:text-gray-100">{title}</DialogTitle>
              {description && <Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</Description>}
            </div>
            {!hideClose && <IconButton label="Close" icon={XMarkIcon} size="sm" onClick={onClose} className="-mr-2 -mt-0.5" />}
          </div>
          {children}
        </DialogPanel>
      </div>
    </HeadlessDialog>
  );
}

export function DialogBody({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cx('min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4', className)}>{children}</div>;
}

export function DialogFooter({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cx('flex flex-wrap items-center justify-end gap-2 rounded-b-2xl border-t border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950/50 px-6 py-3', className)}>
      {children}
    </div>
  );
}

/** Pushes what follows it in a DialogFooter to the right, e.g. after an "Add another" switch. */
export function DialogFooterSpacer() {
  return <span className="flex-1" />;
}
